import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { 
  ArrowRight, Target, Users, Brain, Workflow, Shield, 
  MapPin, TrendingUp, Store, Sparkles, Eye, Phone, Instagram, Map
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';

// Video Assets - Transparent WebM
const PIRONE_VIDEO = "/assets/pirone-logo-video.webm";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.15 } }
};

// Section wrapper
const Section = ({ children, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// 3D Tilt Card Component
const TiltCard = ({ children, className = '', glowColor = 'rgba(56, 189, 248, 0.1)' }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0.5, y: 0.5 });
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setMousePosition({ x, y });
  };

  return (
    <div
      ref={cardRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseMove={handleMouseMove}
      className={`relative ${className}`}
      style={{
        transform: isHovered 
          ? `perspective(1000px) rotateX(${(mousePosition.y - 0.5) * -10}deg) rotateY(${(mousePosition.x - 0.5) * 10}deg)`
          : 'perspective(1000px) rotateX(0deg) rotateY(0deg)',
        transition: 'transform 0.3s ease-out'
      }}
    >
      <div 
        className="absolute inset-0 rounded-3xl pointer-events-none transition-opacity duration-300"
        style={{
          opacity: isHovered ? 1 : 0,
          boxShadow: `inset 0 0 50px ${glowColor}, 0 0 40px ${glowColor}`
        }}
      />
      {children}
    </div>
  );
};

// Animated Counter
const AnimatedStat = ({ value, label }) => {
  return (
    <div className="text-center">
      <div className="text-4xl md:text-5xl font-bold text-[#38BDF8] mb-2">{value}</div>
      <div className="text-white/40 text-sm">{label}</div>
    </div>
  );
};

const AboutPage = () => {
  const { t } = useLanguage();

  // Icons for principles (can't be stored in JSON)
  const principleIcons = [Target, Workflow, Brain, Shield];
  
  // Icons for local business features
  const localFeatureIcons = [Map, Instagram, Phone, TrendingUp];

  return (
    <div className="min-h-screen bg-transparent pt-32 pb-24">
      {/* Animated Background elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {/* Top gradient */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[600px]"
          style={{ background: 'radial-gradient(ellipse at center top, rgba(56, 189, 248, 0.08) 0%, transparent 60%)' }}
        />
        {/* Floating orbs */}
        <motion.div 
          animate={{ y: [0, -20, 0], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-[20%] left-[10%] w-64 h-64 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(56, 189, 248, 0.1) 0%, transparent 70%)' }}
        />
        <motion.div 
          animate={{ y: [0, 30, 0], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          className="absolute top-[50%] right-[5%] w-96 h-96 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(16, 185, 129, 0.08) 0%, transparent 70%)' }}
        />
        {/* Noise texture */}
        <div className="absolute inset-0 opacity-[0.015]"
          style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }}
        />
      </div>

      <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
        {/* Badge */}
        <Section className="text-center mb-8">
          <motion.div variants={fadeInUp}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.03] border border-white/[0.06] backdrop-blur-sm">
              <Users size={14} className="text-[#38BDF8]" />
              <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/40">
                {t('about.badge')}
              </span>
            </div>
          </motion.div>
        </Section>

        {/* Manifesto - Big Typography with Highlight */}
        <Section className="mb-24">
          <div className="text-center max-w-[900px] mx-auto">
            <motion.div variants={scaleIn} className="mb-8">
              <video 
                src={PIRONE_VIDEO}
                autoPlay
                loop
                muted
                playsInline
                className="h-16 md:h-20 w-auto mx-auto"
              />
            </motion.div>
            
            <motion.h1 
              variants={fadeInUp} 
              className="text-4xl md:text-6xl lg:text-7xl font-semibold text-white/40 mb-8 tracking-tight leading-[1.1]"
            >
              {t('about.manifestoLine2Part1')}
              <span className="text-[#38BDF8] relative">
                {t('about.manifestoLine2Highlight')}
                <motion.span 
                  className="absolute -bottom-2 left-0 right-0 h-1 bg-[#38BDF8]/30 rounded-full"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: 1, duration: 0.8 }}
                />
              </span>
              {t('about.manifestoLine2Part2')}
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-lg md:text-xl text-white/50 leading-relaxed max-w-[700px] mx-auto"
            >
              {t('about.manifestoDesc')}
            </motion.p>
          </div>
        </Section>

        {/* Stats Bar */}
        <Section className="mb-32">
          <motion.div 
            variants={fadeInUp}
            className="flex justify-center gap-16 md:gap-24 py-8 px-8 rounded-3xl bg-white/[0.02] border border-white/[0.04]"
          >
            <AnimatedStat value={t('about.stat1Value')} label={t('about.stat1Label')} />
            <AnimatedStat value={t('about.stat2Value')} label={t('about.stat2Label')} />
            <AnimatedStat value={t('about.stat3Value')} label={t('about.stat3Label')} />
          </motion.div>
        </Section>

        {/* Principles Grid */}
        <Section className="mb-32">
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-white tracking-tight">
              {t('about.principlesTitle')}
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((num, i) => {
              const IconComponent = principleIcons[i];
              return (
                <motion.div key={i} variants={fadeInUp}>
                  <TiltCard className="h-full">
                    <div className="p-8 rounded-3xl bg-[#0A0A0A] border border-white/[0.06] h-full">
                      <div className="w-12 h-12 rounded-2xl bg-[#38BDF8]/10 border border-[#38BDF8]/20 flex items-center justify-center mb-6">
                        <IconComponent size={22} className="text-[#38BDF8]" />
                      </div>
                      <h3 className="text-xl font-semibold text-white mb-3">
                        {t(`about.principle${num}Title`)}
                      </h3>
                      <p className="text-white/40 leading-relaxed">
                        {t(`about.principle${num}Desc`)}
                      </p>
                    </div>
                  </TiltCard>
                </motion.div>
              );
            })}
          </div>
        </Section>

        {/* Local Business Section */}
        <Section className="mb-32">
          <div className="relative rounded-3xl overflow-hidden">
            {/* Gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 via-transparent to-[#38BDF8]/10" />
            
            <div className="relative p-8 md:p-12">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                {/* Left - Content */}
                <motion.div variants={fadeInUp}>
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-6">
                    <Store size={14} className="text-emerald-400" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-emerald-400">
                      {t('about.localBusinessBadge')}
                    </span>
                  </div>
                  
                  <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4 tracking-tight">
                    {t('about.localBusinessTitle')}
                  </h2>
                  <p className="text-lg text-emerald-400/80 mb-4">
                    {t('about.localBusinessSubtitle')}
                  </p>
                  <p className="text-white/50 leading-relaxed mb-8">
                    {t('about.localBusinessDesc')}
                  </p>
                  
                  <Link
                    to="/new-jersey"
                    className="group inline-flex items-center gap-3 h-12 px-6 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold rounded-full transition-all duration-300"
                  >
                    <span>{t('about.localBusinessCta')}</span>
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </motion.div>
                
                {/* Right - Features */}
                <motion.div variants={fadeInUp} className="grid grid-cols-2 gap-4">
                  {[1, 2, 3, 4].map((num, i) => {
                    const IconComponent = localFeatureIcons[i];
                    return (
                      <div 
                        key={i}
                        className="p-5 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:border-emerald-500/30 transition-colors duration-300"
                      >
                        <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-4">
                          <IconComponent size={18} className="text-emerald-400" />
                        </div>
                        <h4 className="text-white font-semibold mb-1">
                          {t(`about.localFeature${num}Title`)}
                        </h4>
                        <p className="text-white/40 text-sm">
                          {t(`about.localFeature${num}Desc`)}
                        </p>
                      </div>
                    );
                  })}
                </motion.div>
              </div>
            </div>
          </div>
        </Section>

        {/* Philosophy */}
        <Section className="mb-32">
          <motion.div variants={fadeInUp} className="text-center max-w-[800px] mx-auto">
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-8 tracking-tight">
              {t('about.philosophyTitle')}
            </h2>
            <blockquote className="text-2xl md:text-3xl text-white/60 italic mb-12 leading-relaxed">
              {t('about.philosophyQuote')}
            </blockquote>
            <div className="flex flex-wrap justify-center gap-4">
              {[1, 2, 3, 4].map((num) => (
                <span 
                  key={num}
                  className="px-5 py-2.5 rounded-full bg-white/[0.03] border border-white/[0.08] text-white/50 text-sm"
                >
                  {t(`about.philosophyPoint${num}`)}
                </span>
              ))}
            </div>
          </motion.div>
        </Section>

        {/* Capabilities section removed per user request */}


        {/* CTA */}
        <Section>
          <motion.div 
            variants={fadeInUp}
            className="text-center py-16 px-8 rounded-3xl bg-gradient-to-b from-white/[0.03] to-transparent border border-white/[0.06]"
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-8 tracking-tight">
              {t('about.ctaTitle')}
            </h2>
            <Link
              to="/contact"
              className="group inline-flex items-center gap-3 h-14 px-10 bg-white text-black font-semibold rounded-full transition-all duration-300 hover:scale-105 hover:shadow-[0_0_50px_rgba(255,255,255,0.3)]"
            >
              <span>{t('about.ctaButton')}</span>
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </Section>
      </div>
    </div>
  );
};

export default AboutPage;
